import React from "react";
import PropTypes from "prop-types";

import { Table, Button, Link } from "@sunwing/wvi-components";

import { Icon } from "@sunwing/icons/westjet-vacations";

const TableConnector = ({ data, language }) => {
	switch (data?.type) {
		case "pricing":
			return <PricingTable data={data} language={language} />;
		case "additional":
			return <AdditionalTable data={data} />;
		case "data":
		default:
			return <DataTable data={data} language={language} />;
	}
};

const DataTable = ({ data }) => {
	return (
		<Table>
			<Table.Header>
				{data.headers.map(header => (
					<Table.HeaderCell key={header.title} title={header.title} />
				))}
			</Table.Header>
			<Table.Body>
				<Table.Item>
					{data.rows.map(row => (
						<Table.Row key={row.title}>
							{row.columns.map(column =>
								column.type === "header" ? (
									<Table.RowHeaderCell key={column.text} span={3}>
										{column.text}
									</Table.RowHeaderCell>
								) : (
									<Table.Cell key={column.text} span={1}>
										{column.text}
									</Table.Cell>
								)
							)}
						</Table.Row>
					))}
				</Table.Item>
			</Table.Body>
		</Table>
	);
};

const PricingTable = ({ data }) => {
	return (
		<Table>
			<Table.Header>
				{data.headers.map(header => (
					<Table.HeaderCell key={header.title} eyebrow={header.eyebrow} title={header.title} />
				))}
			</Table.Header>
			<Table.Body>
				<Table.Item>
					{data.rows.map(row =>
						row.type !== "pricing" ? (
							<Table.Row key={row.type}>
								{row.columns.map(column => (
									<Table.Cell key={column.text} icon={column?.icon}>
										{column.text}
									</Table.Cell>
								))}
							</Table.Row>
						) : (
							<Table.PricingRow key={row.type}>
								{row.columns.map(column => (
									<Table.PricingCell key={column.text}>
										{column.eyebrow && (
											<Table.PricingCellEyebrow>{column.eyebrow}</Table.PricingCellEyebrow>
										)}
										<Table.PricingCellPrice icon={column?.icon} price={column.price} />
										{column.body && column.body.text && (
											<Table.PricingCellBody>
												{column.body &&
													column.body.text.map((text, index) =>
														index === column.body.text?.length - 1 ? (
															<span key={text}>
																{text}
																<Icon name={column.body.icon} />
															</span>
														) : (
															<p key={text}>{text}</p>
														)
													)}
											</Table.PricingCellBody>
										)}
									</Table.PricingCell>
								))}
							</Table.PricingRow>
						)
					)}
				</Table.Item>
				<Table.Item>
					<Table.ActionRow>
						{data.footer &&
							data.footer.map(footer => (
								<Table.ActionCell key={footer.button.text}>
									{footer.button && (
										<Button fullWidth={footer.button.fullWidth}>{footer.button.text}</Button>
									)}
									{footer.body &&
										footer.body.map(
											body =>
												body.type === "link" && (
													<Link key={body.text} href={body.to} icon={body.icon}>
														{body.text}
													</Link>
												)
										)}
								</Table.ActionCell>
							))}
					</Table.ActionRow>
				</Table.Item>
			</Table.Body>
		</Table>
	);
};

const AdditionalTable = ({ data }) => {
	return (
		<Table>
			<Table.Header>
				{data.headers.map(header => (
					<Table.HeaderCell key={header.title} title={header.title} />
				))}
			</Table.Header>
			<Table.Body>
				<Table.Item>
					{data.rows.map(row => (
						<Table.Row key={row}>
							{row.columns.map(column => (
								<Table.Cell key={column.text} colSpan={column?.span} icon={column?.icon}>
									{column.text}
								</Table.Cell>
							))}
						</Table.Row>
					))}
				</Table.Item>

				<Table.Item>
					<Table.ActionRow>
						{data.footer?.map(footer =>
							footer.button ? (
								<Table.ActionCell key={footer.button.text} colSpan={footer.span}>
									<Button fullWidth={footer.button.fullWidth}>{footer.button.text}</Button>
								</Table.ActionCell>
							) : (
								<Table.PricingCell
									key={footer.body.price}
									colSpan={footer.span}
									align={footer?.align}>
									{footer.price && <Table.PricingCellPrice price={footer.price} />}
									{footer.body && footer.body.text && (
										<Table.PricingCellBody>
											{footer.body.text.map((text, index) =>
												index === footer.body.text.length - 1 ? (
													<span key={text}>
														{text}
														<Icon name={footer.body.icon} />
													</span>
												) : (
													<p key={text}>{text}</p>
												)
											)}
										</Table.PricingCellBody>
									)}
								</Table.PricingCell>
							)
						)}
					</Table.ActionRow>
				</Table.Item>
			</Table.Body>
		</Table>
	);
};

DataTable.propTypes = {
	data: PropTypes.object.isRequired,
};

PricingTable.propTypes = {
	data: PropTypes.object.isRequired,
};

AdditionalTable.propTypes = {
	data: PropTypes.object.isRequired,
};

TableConnector.propTypes = {
	data: PropTypes.object.isRequired,
	language: PropTypes.string.isRequired,
};

export { TableConnector };
